<template>
  <div class="wrapper">
    <div class="user-view" v-if="user.user">
      <div class="user-header">
        <div class="profile-image" :style="{ backgroundImage: `url('${user.user.profileImgUrl}')` }"></div>
        <div class="user-info">
          <div class="name">{{ user.user.name }} {{ user.user.lastName }}</div>
          <div class="contact-info">
            <div class="email">{{ user.user.email }}</div>
            <div class="phone">{{ user.user.phone }}</div>
          </div>
          <div class="user-stats">
            <div class="user-stat">
              <span>{{ user.investments.length }}</span> inversiones totales
            </div>
            <div class="user-stat">
              <span>{{ formatMoney(totalInvestment) }}</span> total invertido
            </div>
          </div>
        </div>
        <div class="completed-percentage">
          <span>{{ progress }}</span>
          perfil completado
        </div>
      </div>
      <div class="user-actions">
        <div class="action-card" @click="changeUser(user.user.isLocked ? 'unlock' : 'lock')">
          <div class="action-title">
            <img src="@/assets/verified.svg" alt="">
            {{ user.user.isLocked ? 'Desverificar cuenta' : 'Verificar cuenta' }}
          </div>
          Los datos del usuario quedan verificados y bloqueados para su edición, el usuario no podra realizar cambios.
        </div>
        <div class="action-card" @click="changeUser(user.user.isBlocked ? 'unblock' : 'block')">
          <div class="action-title">
            <img src="@/assets/padlock.svg" alt="">
            {{ user.user.isBlocked ? 'Desbloquear usuario' : 'Bloquear usuario' }}
          </div>
          El usuario no podra acceder a Flagship con su cuenta. No es posible crear otra cuenta con la misma información.
        </div>
        <div class="action-card" @click="deleteUser">
          <div class="action-title">
            <img src="@/assets/cancel.svg" alt="">
            Eliminar usuario
          </div>
          Toda la información del usuario incluyendo sus pagos e inversiones sera eliminada. Esta acción no es reversible.
        </div>
      </div>
      <div class="user-items">
        <div class="section-title">Información de usuario</div>
        <div class="column">
          <div class="item">
            <div class="item-title">Fecha de nacimiento</div>
            <div class="item-value">{{ moment(user.user.birthday).format("MMM Do YYYY") }}</div>
          </div>
          <div class="item">
            <div class="item-title">RFC</div>
            <div class="item-value">{{ user.user.rfc }}</div>
          </div>
          <div class="item">
            <div class="item-title">CURP</div>
            <div class="item-value">{{ user.user.curp }}</div>
          </div>
          <div class="item">
            <div class="item-title">Nacionalidad</div>
            <div class="item-value">{{ user.user.nationality }}</div>
          </div>
          <div class="item">
            <div class="item-title">Inversionista calificado</div>
            <div class="item-value">{{ user.user.isQualifiedInvestor ? 'Si' : 'No' }}</div>
          </div>
          <div class="item">
            <div class="item-title">PPE</div>
            <div class="item-value">{{ user.user.isPep ? 'Si' : 'No' }}</div>
          </div>
          <div class="item">
            <div class="item-title">Relacionado a una PPE</div>
            <div class="item-value">{{ user.user.isPepRelated ? 'Si' : 'No' }}</div>
          </div>
          <div class="item">
            <div class="item-title">Lugar de trabajo</div>
            <div class="item-value">{{ user.user.workCompany }}</div>
          </div>
          <div class="item">
            <div class="item-title">Ingresos</div>
            <div class="item-value">{{ user.user.averageIncome }}</div>
          </div>
        </div>
        <div class="column">
          <div class="item">
            <div class="item-title">Dirección</div>
            <div class="item-value">{{ user.user.address }}</div>
          </div>
          <div class="item">
            <div class="item-title">Colonia</div>
            <div class="item-value">{{ user.user.settlement }}</div>
          </div>
          <div class="item">
            <div class="item-title">Ciudad</div>
            <div class="item-value">{{ user.user.municipality }}</div>
          </div>
          <div class="item">
            <div class="item-title">Estado</div>
            <div class="item-value">{{ user.user.state }}</div>
          </div>
          <div class="item">
            <div class="item-title">Pais</div>
            <div class="item-value">{{ user.user.country }}</div>
          </div>
          <div class="item">
            <div class="item-title">Codigo Postal</div>
            <div class="item-value">{{ user.user.postalCode }}</div>
          </div>
          <div class="item">
            <div class="item-title">Teléfono</div>
            <div class="item-value">{{ user.user.phone }}</div>
          </div>
          <div class="item">
            <div class="item-title">CLABE</div>
            <div class="item-value">{{ user.user.accountClabe }}</div>
          </div>
        </div>
      </div>
      <div class="uploaded-items">
        <div class="section-title">Documentación</div>
        <div class="column">
          <div style="margin-bottom: 15px;">Identificación oficial:</div>
          <img class="id-img" v-if="idBase64" :src="`data:image/jpg;base64, ${idBase64}`" accept="image/jpeg">
        </div>
      </div>
      <div class="user-investments">
        <div class="section-title">Inversiones en Flagship.mx</div>
        <div class="investment" v-for="investment in user.investments" :key="investment.id">
          <div class="investment-image" :style="{ backgroundImage: `url('${investment.project.mainImageUrl}')` }"></div>
          <div class="investment-info">
            <div class="investment-name">
              {{ investment.project.name }}
            </div>
            <div class="">
              {{ investment.project.location }}
            </div>
          </div>
          <div class="investment-total">
            {{ formatMoney(investment.investment) }}
          </div>
          <div class="investment-creation">
            {{ moment(investment.created_at).format('MMMM Do YYYY, h:mm a') }}
          </div>
        </div>
      </div>
      <div class="user-investments">
        <div class="section-title">Interes en invertir</div>
        <div class="investment" v-for="investment in user.preinvestments" :key="investment.id">
          <div class="investment-image" :style="{ backgroundImage: `url('${investment.project.mainImageUrl}')` }"></div>
          <div class="investment-info">
            <div class="investment-name">
              {{ investment.project.name }}
            </div>
            <div class="">
              {{ investment.project.location }}
            </div>
          </div>
          <div class="investment-total">
            {{ formatMoney(investment.investment) }}
          </div>
          <div class="investment-creation">
            {{ moment(investment.created_at).format('MMMM Do YYYY, h:mm a') }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import axios from 'axios';
import formatMixin from '@/mixins/formatMixin';
import { createNamespacedHelpers } from 'vuex';

const { mapActions, mapState } = createNamespacedHelpers('admin');

export default {
  data() {
    return {
      moment,
      idBase64: null,
    };
  },
  mixins: [formatMixin],
  computed: {
    progress() {
      if (this.user) {
        const attributes = [
          'name',
          'lastName',
          'birthday',
          'rfc',
          'curp',
          'nationality',
          'isQualifiedInvestor',
          'isPep',
          'isPepRelated',
          'workCompany',
          'averageIncome',
          'address',
          'settlement',
          'municipality',
          'state',
          'country',
          'postalCode',
          'phone',
          'officialIdUrl',
          'proofOfAdressUrl',
          'accountClabe',
        ];
        const total = attributes.length;
        let completed = 0;

        for (let i = 0; i < total; i += 1) {
          const value = this.user.user[attributes[i]];
          if (value !== null) {
            completed += 1;
          }
        }
        const percentage = (completed / total) * 100;
        return `${percentage.toFixed(1)}%`;
      }
      return '0%';
    },
    totalInvestment() {
      if (!this.user) {
        return 0;
      }
      let total = 0;
      for (let i = 0; i < this.user.investments.length; i += 1) {
        const element = this.user.investments[i];
        console.log(element);
        total += parseInt(element.investment, 10);
      }
      return total;
    },
    ...mapState([
      'user',
    ]),
  },
  methods: {
    stylizeNumber(number) {
      if (number) {
        return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      }
      return 0;
    },
    changeUser(action) {
      this.editUser({
        action,
        id: this.$route.params.userId,
      });
    },
    loadImage() {
      axios.get(`${process.env.VUE_APP_SERVER_URL}/admin/user-id/${this.user.user.id}`).then(({ data }) => {
        this.idBase64 = data;
      });
    },
    async deleteUser() {
      await this.removeUser(this.$route.params.userId);
      this.$router.push('/admin/users');
    },
    ...mapActions([
      'getUser',
      'editUser',
      'removeUser',
    ]),
  },
  mounted() {
    const { userId } = this.$route.params;
    this.getUser(userId).then(() => {
      if (this.user.user.officialIdUrl) {
        this.loadImage();
      }
    });
    moment.locale('es');
  },
};
</script>

<style lang="scss" scoped>
.wrapper {
  font-size: 17px;
  width: 100%;
  display: flex;
  justify-content: center;
  background-color: #ffffff;
  min-height: 100vh;
  font-family: "Inter", sans-serif;
}
.user-view {
  width: 900px;
  padding-top: 100px;
  padding-bottom: 50px;
  display: flex;
  flex-direction: column;
}
.user-header {
  display: flex;
  margin-bottom: 40px;
  align-items: center;
}
.contact-info {
  display: flex;
}
.profile-image {
  width: 16%;
  padding-bottom: 16%;
  border-radius: 50%;
  background-size: cover;
  background-position: center;
  margin-right: 40px;
}
.user-info {
  flex-grow: 1;
}
.name {
  font-size: 38px;
  font-weight: 700;
  letter-spacing: 0.64px;
  margin-bottom: 14px;
}
.email {
  margin-right: 20px;
}
.user-stats {
  margin-top: 15px;
  display: flex;
}
.user-stat {
  display: flex;
  flex-direction: column;
  span {
    font-size: 24px;
    font-weight: 700;
    margin-bottom: 3px;
  }
  & + .user-stat {
    margin-left: 30px;
  }
}
.completed-percentage {
  display: flex;
  justify-content: flex-end;
  flex-direction: column;
  align-items: center;
  white-space: nowrap;

  span {
    font-weight: 700;
    font-size: 28px;
    margin-bottom: 5px;
  }
}
.user-items {
  display: flex;
  flex-wrap: wrap;
}
.uploaded-items {
  display: flex;
  flex-wrap: wrap;
  margin-top: 16px;
}
.section-title {
  width: 100%;
  font-weight: 700;
  font-size: 24px;
  margin-bottom: 20px;
}
.column {
  width: 50%;
}
.item {
  display: flex;
  margin-bottom: 10px;
}
.item-title {
  width: 45%;
}
.item-value {
  flex-grow: 1;
  color: lighten($color: #000000, $amount: 60)
}
.user-investments {
  margin-top: 30px;
}
.user-actions {
  display: flex;
  justify-content: space-between;
  margin-bottom: 30px;
}
.action-card {
  width: 32%;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 10px;
  padding: 20px 20px;
  line-height: 1.2em;
  cursor: pointer;

  img {
    height: 40px;
    margin-right: 12px;
  }
}
.action-title {
  margin-bottom: 14px;
  font-weight: 700;
  display: flex;
  align-items: center;
}
.investment {
  display: flex;
  border: 1px solid rgba(0, 0, 0, 0.12);
  align-items: center;
  justify-content: space-between;
  padding: 16px;

  & + .investment {
    margin-top: 20px;
  }
}
.investment-image {
  width: 50px;
  padding-bottom: 50px;
  background-size: cover;
  background-position: center;
}
.investment-name {
  font-weight: 700;
  margin-bottom: 4px;
}
.investment-info {
  width: 35%;
}
.investment-total {
  width: 17%;
}
.investment-creation {
  width: 35%;
}
.id-img {
  width: 30%;
  margin: auto;
}
@media only screen and (max-width: 900px) {
  .user-view {
    width: 95%;
  }
}
@media only screen and (max-width: 700px) {
  .profile-image {
    display: none;
  }
  .completed-percentage {
    display: none;
  }
  .user-actions {
    flex-direction: column;;
  }
  .action-card {
    width: 100%;
    margin: 10px 0;
  }
  .user-items {
    flex-direction: column;
  }
  .column {
    width: 100%;
  }
}
</style>
